import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, History, Search, RefreshCw, Send, Download, Mail, Plus, Trash2, Save } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';

const ACTION_COLOR = {
  login:               'bg-emerald-100 text-emerald-700',
  expense_create:      'bg-sky-100 text-sky-700',
  expense_delete:      'bg-red-100 text-red-700',
  staff_create:        'bg-teal-100 text-teal-700',
  staff_delete:        'bg-red-100 text-red-700',
  admin_user_create:   'bg-violet-100 text-violet-700',
  admin_user_update:   'bg-amber-100 text-amber-700',
  admin_user_delete:   'bg-red-100 text-red-700',
  daily_report_recipients_update: 'bg-teal-100 text-teal-700',
};

const ActivityLogPage = () => {
  const { authHeader, user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmail, setFilterEmail] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [err, setErr] = useState('');

  // Daily-report recipients editor state
  const [recipients, setRecipients] = useState([]);
  const [recipientsSource, setRecipientsSource] = useState('env_fallback');
  const [envFallback, setEnvFallback] = useState('');
  const [newRecipient, setNewRecipient] = useState('');
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [recipientsMsg, setRecipientsMsg] = useState('');
  const [recipientsErr, setRecipientsErr] = useState('');

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEmail) params.append('user_email', filterEmail);
      if (filterAction) params.append('action', filterAction);
      params.append('limit', '300');
      const { data } = await axios.get(`${API}/activity-log?${params.toString()}`, { headers: authHeader() });
      setRows(data || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, filterEmail, filterAction]);

  const fetchRecipients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/daily-report/recipients`, { headers: authHeader() });
      // When the backend is returning the env fallback we don't want to
      // pre-populate the editor with it — otherwise a user who just adds
      // one email would end up saving env-fallback + their email as the
      // custom list, which is surprising. Show empty and let the header
      // pill communicate what's actually in effect.
      const src = data.source || 'env_fallback';
      setRecipients(src === 'configured' ? (data.recipients || []) : []);
      setRecipientsSource(src);
      setEnvFallback(data.env_fallback || '');
    } catch (e) { setRecipientsErr(formatErr(e)); }
  }, [authHeader]);

  useEffect(() => { fetchLog(); }, [fetchLog]);
  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const addRecipient = () => {
    const v = (newRecipient || '').trim().toLowerCase();
    if (!v) return;
    // Basic email shape check — backend does the strict EmailStr parse.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setRecipientsErr('Please enter a valid email address.');
      return;
    }
    if (recipients.includes(v)) {
      setRecipientsErr('That email is already in the list.');
      return;
    }
    setRecipientsErr('');
    setRecipients((r) => [...r, v]);
    setNewRecipient('');
  };

  const removeRecipient = (email) => setRecipients((r) => r.filter((e) => e !== email));

  const saveRecipients = async () => {
    setSavingRecipients(true);
    setRecipientsErr(''); setRecipientsMsg('');
    try {
      const { data } = await axios.put(
        `${API}/admin/daily-report/recipients`,
        { recipients },
        { headers: authHeader() },
      );
      setRecipientsSource(data.source);
      setRecipientsMsg(
        data.source === 'configured'
          ? `Saved. Daily report will be sent to ${data.recipients.length} recipient(s).`
          : `List cleared — falling back to ${envFallback || 'the env default'}.`
      );
    } catch (e) { setRecipientsErr(formatErr(e)); }
    finally { setSavingRecipients(false); }
  };

  const runDailyReportNow = async () => {
    if (!window.confirm(
      `Send the daily attendance & OT report email right now (to ${recipients.join(', ') || 'the env fallback'})?`
    )) return;
    try {
      const { data } = await axios.post(`${API}/admin/daily-report/run-now`, {}, { headers: authHeader() });
      const to = Array.isArray(data.to) ? data.to.join(', ') : data.to;
      alert(`Email queued. Sent to: ${to}`);
    } catch (e) { notifyErr(e); }
  };

  const downloadDailyReportPdf = async () => {
    try {
      const res = await axios.get(`${API}/admin/daily-report/pdf`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      // Pull the filename out of Content-Disposition if present, else build one.
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `attendance_${new Date().toISOString().slice(0, 10)}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { notifyErr(e); }
  };

  if (!me?.is_master_admin) {
    return <div className="p-6 text-slate-500 text-sm">Master admin only.</div>;
  }

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="activity-log-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <History className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Activity Log</h1>
          <span className="text-xs text-slate-500">last {rows.length} entries</span>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchLog} className="text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="refresh-log-btn">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={downloadDailyReportPdf} className="text-sm bg-teal-600 hover:bg-teal-700 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="daily-report-pdf-btn">
            <Download size={14} /> Download Daily Report PDF
          </button>
          <button onClick={runDailyReportNow} className="text-sm bg-amber-500 hover:bg-amber-600 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="daily-report-now-btn">
            <Send size={14} /> Send Daily Report Now
          </button>
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="daily-report-recipients-card">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-teal-600" />
            <h2 className="text-sm font-semibold text-slate-900">Daily Report Recipients</h2>
            <span
              className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                recipientsSource === 'configured'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}
              data-testid="recipients-source"
            >
              {recipientsSource === 'configured' ? 'Custom list' : 'Env fallback'}
            </span>
          </div>
          <p className="text-[11px] text-slate-500">
            10:30 IST daily · attendance + OT report is emailed to everyone in this list.
            {envFallback && recipientsSource === 'env_fallback' && (
              <span className="text-slate-400"> · Falling back to <b>{envFallback}</b></span>
            )}
          </p>
        </div>

        {recipients.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="recipients-chips">
            {recipients.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 text-slate-700 text-xs px-2.5 py-1 rounded-full"
                data-testid={`recipient-${email}`}
              >
                {email}
                <button
                  onClick={() => removeRecipient(email)}
                  className="text-slate-400 hover:text-red-600"
                  title="Remove"
                  data-testid={`remove-recipient-${email}`}
                >
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            placeholder="Add email address…"
            value={newRecipient}
            onChange={(e) => setNewRecipient(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
            className={`${inputCls} flex-1 min-w-[220px]`}
            data-testid="recipient-input"
          />
          <button
            onClick={addRecipient}
            className="text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg inline-flex items-center gap-1.5"
            data-testid="add-recipient-btn"
          >
            <Plus size={14} /> Add
          </button>
          <button
            onClick={saveRecipients}
            disabled={savingRecipients}
            className="text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5"
            data-testid="save-recipients-btn"
          >
            {savingRecipients ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {savingRecipients ? 'Saving…' : 'Save'}
          </button>
        </div>

        {recipientsMsg && (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
            {recipientsMsg}
          </div>
        )}
        {recipientsErr && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            {recipientsErr}
          </div>
        )}
      </section>

      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="email" placeholder="Filter by user email…" value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} className={`${inputCls} pl-8 min-w-[260px]`} data-testid="filter-email" />
        </div>
        <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} className={inputCls} data-testid="filter-action">
          <option value="">All actions</option>
          {Object.keys(ACTION_COLOR).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        {(filterEmail || filterAction) && (
          <button onClick={() => { setFilterEmail(''); setFilterAction(''); }} className="text-sm text-slate-500 hover:text-slate-800">Clear</button>
        )}
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No matching activity.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Timestamp</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">User</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Action</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Target</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Details</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const color = ACTION_COLOR[r.action] || 'bg-slate-100 text-slate-700';
                  return (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`log-row-${r.id}`}>
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap text-slate-600">{(r.timestamp || '').replace('T', ' ').slice(0, 19)}</td>
                      <td className="px-3 py-2">
                        <div className="text-slate-800">{r.user_email}</div>
                        <div className="text-[10px] text-slate-500">{r.is_master_admin ? 'master' : r.user_role}</div>
                      </td>
                      <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded ${color}`}>{r.action}</span></td>
                      <td className="px-3 py-2 text-xs text-slate-500">{r.target_type || '—'}{r.target_id ? ` · ${r.target_id.slice(0, 8)}` : ''}</td>
                      <td className="px-3 py-2 text-xs text-slate-500 max-w-md truncate" title={JSON.stringify(r.metadata)}>{Object.keys(r.metadata || {}).length ? JSON.stringify(r.metadata) : '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-400 font-mono">{r.ip || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogPage;
